import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Star,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  CircleCheck,
  RotateCcw,
  Trash2,
  Settings,
  LogOut,
  Save,
  BellRing,
  Image,
  Mail,
  Link as LinkIcon,
  Building2,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { translations, fmt } from "@/lib/translations";
import { useSeo } from "@/lib/seo";
import { Brand, GemStar, LangToggle, LoadingScreen, LogoMark, detectLang, saveLang } from "@/components/brand";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Alert sound via WebAudio. Browsers only allow audio after a user gesture,
   so one shared AudioContext is created/resumed from a click ("unlock"). */
let audioCtx = null;

const unlockAudio = async () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state !== "running") await audioCtx.resume();
    return audioCtx.state === "running";
  } catch (e) {
    return false;
  }
};

const audioReady = () => !!audioCtx && audioCtx.state === "running";

/* Front-desk bell: two bright strikes built from inharmonic partials (like a
   real counter bell), then a soft rising "attention" tone. Loud but pleasant. */
const strikeBell = (ctx, out, t0, base, level) => {
  // partial ratios of a small hand bell + their relative loudness / decay
  [
    [1, 1, 1.6],
    [2.76, 0.55, 0.9],
    [5.4, 0.3, 0.5],
    [8.93, 0.14, 0.3],
  ].forEach(([ratio, amp, decay]) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = base * ratio;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(level * amp, t0 + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + decay);
    o.connect(g).connect(out);
    o.start(t0);
    o.stop(t0 + decay + 0.05);
  });
};

const playChime = () => {
  if (!audioReady()) return false;
  try {
    const ctx = audioCtx;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.ratio.value = 4;
    const master = ctx.createGain();
    master.gain.value = 0.9;
    master.connect(comp).connect(ctx.destination);
    const t = ctx.currentTime + 0.02;
    // ding-ding
    strikeBell(ctx, master, t, 1318.5, 0.55); // E6
    strikeBell(ctx, master, t + 0.28, 1318.5, 0.5);
    // short rising tail so it reads as "new alert", not a doorbell
    [659.25, 830.61, 987.77].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "triangle";
      o.frequency.value = f;
      const s0 = t + 0.75 + i * 0.12;
      g.gain.setValueAtTime(0.0001, s0);
      g.gain.exponentialRampToValueAtTime(0.28, s0 + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, s0 + 0.35);
      o.connect(g).connect(master);
      o.start(s0);
      o.stop(s0 + 0.4);
    });
    return true;
  } catch (e) {
    return false;
  }
};

const readJSON = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch (e) {
    return null;
  }
};

const MiniStars = ({ rating }) => (
  <div className="flex gap-0.5" aria-label={`${rating}/5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} style={{ width: 16, height: 16, display: "inline-block" }}>
        <GemStar lit={n <= rating} />
      </span>
    ))}
  </div>
);

// ---------- Login ----------
const AdminLogin = ({ onLogin, lang, setLang }) => {
  const [hotelSlug, setHotelSlug] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const t = translations[lang];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { hotel_slug: hotelSlug.trim(), password });
      if (res.data.success) {
        try {
          localStorage.setItem("adminToken", res.data.token);
          localStorage.setItem("adminHotel", JSON.stringify(res.data.hotel));
        } catch (err) {
          /* storage unavailable — session-only login */
        }
        onLogin(res.data.token, res.data.hotel);
      }
    } catch (error) {
      toast.error(t.invalidPassword);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="scene flex flex-col" style={{ minHeight: "100dvh" }}>
      <div className="floor" />
      <header className="wrap flex items-center justify-between pt-6">
        <Brand />
        <LangToggle lang={lang} onChange={setLang} testId="admin-language-toggle" />
      </header>
      <main className="wrap flex-1 grid place-items-center py-12">
        <motion.div
          initial={{ opacity: 0, y: 24, rotateX: 10 }}
          animate={{ opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="panel panel-rim w-full"
          style={{ maxWidth: 420, padding: "2.4rem 2rem", transformPerspective: 1200 }}
        >
          <div className="step-token mb-6" style={{ width: 56, height: 56 }}>
            <Building2 size={24} />
          </div>
          <h1 className="display" style={{ fontSize: "2.4rem" }}>
            {t.adminLogin}
          </h1>
          <p className="muted mt-2 mb-8">{t.adminLoginSub}</p>

          <form onSubmit={handleSubmit} className="grid gap-5">
            <div>
              <label className="field-label" htmlFor="slug">{t.hotelId}</label>
              <input
                id="slug"
                type="text"
                value={hotelSlug}
                onChange={(e) => setHotelSlug(e.target.value.toLowerCase())}
                placeholder="ornek-otel"
                className="field mono"
                autoCapitalize="none"
                autoComplete="username"
                data-testid="admin-hotel-slug-input"
                autoFocus
              />
            </div>
            <div>
              <label className="field-label" htmlFor="pw">{t.password}</label>
              <input
                id="pw"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t.enterPassword}
                className="field"
                autoComplete="current-password"
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !hotelSlug || !password}
              className="btn btn-primary btn-block mt-2"
              data-testid="admin-login-button"
            >
              {isLoading ? "…" : t.login}
            </button>
          </form>
        </motion.div>
      </main>
    </div>
  );
};

// ---------- Dashboard ----------
export const AdminDashboard = () => {
  useSeo({ noindex: true });
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem("adminToken") || "";
    } catch (e) {
      return "";
    }
  });
  const [hotel, setHotel] = useState(() => readJSON("adminHotel"));
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [lang, setLangState] = useState(detectLang);
  const [copied, setCopied] = useState(false);
  const [queue, setQueue] = useState([]); // unacknowledged low-rating alerts
  const [soundOn, setSoundOn] = useState(false);
  const seenIds = useRef(new Set());
  const baseTitle = useRef(typeof document !== "undefined" ? document.title : "RateGuard");
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const t = translations[lang];
  const setLang = (code) => {
    setLangState(code);
    saveLang(code);
  };

  const headers = useMemo(() => ({ headers: { "X-Admin-Token": token } }), [token]);

  const handleLogout = useCallback(() => {
    try {
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminHotel");
    } catch (e) {
      /* ignore */
    }
    setToken("");
    setHotel(null);
    setFeedback([]);
    setStats(null);
    setSettings(null);
  }, []);

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [feedbackRes, statsRes, hotelRes] = await Promise.all([
        axios.get(`${API}/admin/feedback`, headers),
        axios.get(`${API}/admin/feedback/stats`, headers),
        axios.get(`${API}/admin/hotel`, headers),
      ]);
      setFeedback(feedbackRes.data);
      setStats(statsRes.data);
      setSettings(hotelRes.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) handleLogout();
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [token, headers, handleLogout]);

  const fetchAlerts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/alerts/pending`, headers);
      const fresh = (res.data || []).filter((a) => !seenIds.current.has(a.id));
      if (fresh.length > 0) {
        fresh.forEach((a) => seenIds.current.add(a.id));
        setQueue((q) => [...q, ...fresh]);
        playChime();
        try {
          if ("Notification" in window && Notification.permission === "granted") {
            fresh.forEach((a) => {
              const n = new Notification(fmt(t.lowAlert, { room: a.room_number || "—", stars: a.rating }), {
                body: a.message || "",
                tag: a.id,
                requireInteraction: true,
              });
              n.onclick = () => {
                window.focus();
                n.close();
              };
            });
          }
        } catch (e) {
          /* notifications unavailable */
        }
        // Mark as delivered on the server; the on-screen alert stays until staff dismiss it.
        await axios.post(`${API}/admin/alerts/acknowledge`, fresh.map((a) => a.id), headers);
        fetchData();
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) handleLogout();
    }
  }, [token, headers, fetchData, handleLogout, t.lowAlert]);

  useEffect(() => {
    if (!token) return undefined;
    fetchData();
    fetchAlerts();
    const id = setInterval(fetchAlerts, 5000);
    return () => clearInterval(id);
  }, [token, fetchData, fetchAlerts]);

  // Keep ringing every 4 s and flash the tab title until every alert is acknowledged.
  useEffect(() => {
    if (queue.length === 0) {
      document.title = baseTitle.current;
      return undefined;
    }
    let flip = false;
    const titleTimer = setInterval(() => {
      flip = !flip;
      document.title = flip ? `🔴 (${queue.length}) ${t.alertTitle}` : baseTitle.current;
    }, 1000);
    const ringTimer = setInterval(playChime, 4000);
    return () => {
      clearInterval(titleTimer);
      clearInterval(ringTimer);
      document.title = baseTitle.current;
    };
  }, [queue.length, t.alertTitle]);

  // Re-unlock audio on the first interaction after a reload if staff enabled sound before.
  useEffect(() => {
    let pref = false;
    try {
      pref = localStorage.getItem("rg-sound") === "1";
    } catch (e) {
      /* ignore */
    }
    if (!pref) return undefined;
    const onGesture = async () => setSoundOn(await unlockAudio());
    window.addEventListener("pointerdown", onGesture, { once: true });
    window.addEventListener("keydown", onGesture, { once: true });
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
  }, []);

  const enableSound = async () => {
    const ok = await unlockAudio();
    setSoundOn(ok);
    if (ok) {
      playChime();
      try {
        localStorage.setItem("rg-sound", "1");
      } catch (e) {
        /* ignore */
      }
    }
    try {
      if ("Notification" in window && Notification.permission === "default") await Notification.requestPermission();
    } catch (e) {
      /* ignore */
    }
  };

  const dismissAlert = (id) => setQueue((q) => q.filter((a) => a.id !== id));

  useEffect(() => {
    if (queue.length === 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setQueue((q) => q.slice(1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [queue.length]);

  const handleLogin = (newToken, hotelData) => {
    setIsLoading(true);
    setHotel(hotelData);
    setToken(newToken);
  };

  const failed = (error) => {
    if ([401, 403].includes(error?.response?.status)) {
      toast.error(t.sessionExpired);
      handleLogout();
    } else {
      toast.error(t.saveFailed);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/feedback/${id}/status?status=${status}`, {}, headers);
      toast.success(t.statusUpdated);
      fetchData();
    } catch (error) {
      failed(error);
    }
  };

  const handleDelete = async (id) => {
    setConfirmId(null);
    try {
      await axios.delete(`${API}/admin/feedback/${id}`, headers);
      toast.success(t.feedbackDeleted);
      fetchData();
    } catch (error) {
      failed(error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const { name, google_maps_url, logo_url, notification_email } = settings;
      await axios.patch(`${API}/admin/hotel`, { name, google_maps_url, logo_url, notification_email }, headers);
      toast.success(t.settingsSaved);
      setShowSettings(false);
      const updated = { ...hotel, name, google_maps_url, logo_url, notification_email };
      setHotel(updated);
      try {
        localStorage.setItem("adminHotel", JSON.stringify(updated));
      } catch (e) {
        /* ignore */
      }
    } catch (error) {
      failed(error);
    }
  };

  const guestLink = hotel?.slug ? `${window.location.origin}/${hotel.slug}/101` : "";
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(guestLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      toast(guestLink);
    }
  };

  const formatDate = (s) =>
    new Date(s).toLocaleString(lang === "tr" ? "tr-TR" : lang === "sr" ? "sr-Latn-RS" : "en-GB", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (!token) return <AdminLogin onLogin={handleLogin} lang={lang} setLang={setLang} />;
  if (isLoading) return <LoadingScreen />;

  const openCount = feedback.filter((f) => f.status !== "resolved" && f.rating <= 3).length;

  const statTiles = stats
    ? [
        { icon: MessageSquare, value: stats.total_feedback, label: t.totalFeedback, id: "total-feedback" },
        { icon: Star, value: stats.average_rating.toFixed(1), label: t.averageRating, id: "avg-rating" },
        { icon: TrendingUp, value: stats.positive_feedback, label: t.positive, id: "positive-feedback", tone: "ok" },
        { icon: TrendingDown, value: stats.negative_feedback, label: t.negative, id: "negative-feedback", tone: "danger" },
      ]
    : [];

  return (
    <div className="scene" style={{ minHeight: "100dvh" }}>
      {/* Top bar */}
      <header
        className="sticky z-30"
        style={{
          top: "env(safe-area-inset-top, 0px)",
          background: "rgba(10,8,18,.75)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <div className="wrap flex items-center justify-between gap-3" style={{ height: 64 }}>
          <Brand to="/admin" size={30} />
          <div className="flex items-center gap-1 sm:gap-2">
            {soundOn ? (
              <span className="chip chip-resolved chip-dot hidden md:inline-flex" data-testid="sound-on">{t.soundOn}</span>
            ) : (
              <button onClick={enableSound} className="btn btn-primary btn-sm" data-testid="enable-sound" title={t.soundHint}>
                <BellRing size={16} /> <span className="hidden sm:inline">{t.soundOff}</span>
              </button>
            )}
            <LangToggle lang={lang} onChange={setLang} testId="admin-language-toggle" />
            <button onClick={handleLogout} className="icon-btn is-danger" title={t.logout} aria-label={t.logout} data-testid="logout-button">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {!soundOn && (
        <div className="wrap" style={{ paddingTop: "1rem" }}>
          <button
            onClick={enableSound}
            className="panel-flat w-full flex items-center gap-3 text-left"
            style={{ padding: "0.9rem 1.1rem", borderColor: "rgba(251,191,36,.35)", background: "rgba(251,191,36,.06)", color: "var(--text)", cursor: "pointer" }}
          >
            <BellRing size={20} className="text-[var(--warn)] shrink-0" />
            <span className="text-sm"><b>{t.soundOff}.</b> <span className="muted">{t.soundHint}</span></span>
          </button>
        </div>
      )}

      {queue.length > 0 && (
        <div className="backdrop" role="alertdialog" aria-modal="true" aria-labelledby="alert-title" style={{ zIndex: 60 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="panel w-full"
            style={{ maxWidth: 480, padding: "1.8rem", borderColor: "rgba(251,113,133,.5)", boxShadow: "0 0 0 1px rgba(251,113,133,.35), 0 30px 80px -20px rgba(251,113,133,.45), 0 24px 48px -18px rgba(0,0,0,.85)" }}
          >
            <span className="pulse-ring" aria-hidden="true" style={{ borderRadius: 22, pointerEvents: "none" }} />
            <div className="flex items-center justify-between gap-3">
              <span className="chip chip-danger chip-dot">{t.alertTitle}</span>
              {queue.length > 1 && <span className="faint text-sm">{fmt(t.alertsWaiting, { n: queue.length })}</span>}
            </div>
            <h2 id="alert-title" className="display mt-4" style={{ fontSize: "2.2rem" }}>
              {t.room} {queue[0].room_number || "—"}
            </h2>
            <div className="mt-2"><MiniStars rating={queue[0].rating} /></div>
            <p className="mt-4" style={{ fontSize: "1.05rem", lineHeight: 1.5 }}>
              {queue[0].message || <span className="faint">{t.noMessage}</span>}
            </p>
            <p className="faint text-sm mt-2">{formatDate(queue[0].created_at)}</p>
            <div className="flex flex-wrap gap-3 mt-6" style={{ position: "relative", zIndex: 2 }}>
              <button className="btn btn-primary flex-1" onClick={() => dismissAlert(queue[0].id)} data-testid="ack-alert" autoFocus>
                <CircleCheck size={18} /> {t.seen}
              </button>
              {queue.length > 1 && (
                <button className="btn btn-secondary" onClick={() => setQueue([])}>{t.seenAll}</button>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <main className="wrap" style={{ paddingBlock: "2rem 4rem" }}>
        {/* Hotel header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8"
        >
          <div className="flex items-center gap-4 min-w-0">
            {hotel?.logo_url ? (
              <div className="panel grid place-items-center shrink-0" style={{ width: 64, height: 64, borderRadius: 18, padding: 10 }}>
                <img src={hotel.logo_url} alt={hotel.name} className="max-h-full max-w-full object-contain" />
              </div>
            ) : (
              <div className="step-token shrink-0" style={{ width: 64, height: 64, fontSize: "2rem", borderRadius: 18 }}>
                {(hotel?.name || "H").charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <div className="eyebrow">{t.feedbackManagement}</div>
              <h1 className="display truncate" style={{ fontSize: "clamp(2rem, 5vw, 2.8rem)" }} data-testid="admin-title">
                {hotel?.name || "Dashboard"}
              </h1>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {guestLink && (
              <button onClick={copyLink} className="panel-flat flex items-center gap-2 min-w-0" style={{ padding: "0 0.9rem", height: 40, color: "var(--text-2)", cursor: "pointer", fontSize: 13 }} title={t.guestLink}>
                <LinkIcon size={14} />
                <span className="mono truncate" style={{ maxWidth: 220 }}>/{hotel.slug}/101</span>
                <span className="chip" style={{ padding: "1px 8px" }}>{copied ? t.copied : t.copy}</span>
              </button>
            )}
            <button
              onClick={() => setShowSettings((s) => !s)}
              className="btn btn-secondary btn-sm"
              aria-expanded={showSettings}
              data-testid="settings-button"
            >
              <Settings size={16} /> {t.settings}
            </button>
            <button
              onClick={() => {
                setIsRefreshing(true);
                fetchData();
              }}
              disabled={isRefreshing}
              className="btn btn-secondary btn-sm"
              data-testid="refresh-button"
            >
              <RefreshCw size={16} className={isRefreshing ? "animate-spin" : ""} /> {t.refresh}
            </button>
          </div>
        </motion.div>

        {/* Settings */}
        <AnimatePresence>
          {showSettings && settings && (
            <motion.section
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden mb-8"
              data-testid="settings-panel"
            >
              <div className="panel" style={{ padding: "1.6rem" }}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="display" style={{ fontSize: "1.8rem" }}>{t.settings}</h2>
                  <button className="icon-btn" onClick={() => setShowSettings(false)} aria-label="Close">
                    <X size={18} />
                  </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  {[
                    { k: "name", label: t.hotelName, icon: Building2, type: "text", tid: "hotel-name-input" },
                    { k: "google_maps_url", label: t.googleMapsUrl, icon: LinkIcon, type: "url", tid: "google-maps-url-input", ph: "https://g.page/r/…/review" },
                    { k: "logo_url", label: t.logoUrl, icon: Image, type: "url", tid: "hotel-logo-url-input", ph: "https://…/logo.png" },
                    { k: "notification_email", label: t.notificationEmail, icon: Mail, type: "email", tid: "notification-email-input" },
                  ].map((f) => (
                    <div key={f.k}>
                      <label className="field-label" htmlFor={f.k}>
                        <f.icon size={14} className="text-[var(--violet-300)]" /> {f.label}
                      </label>
                      <input
                        id={f.k}
                        type={f.type}
                        value={settings[f.k] || ""}
                        placeholder={f.ph}
                        onChange={(e) => setSettings({ ...settings, [f.k]: e.target.value })}
                        className="field"
                        data-testid={f.tid}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex justify-end">
                  <button onClick={handleSaveSettings} className="btn btn-primary btn-sm" data-testid="save-settings-button">
                    <Save size={16} /> {t.saveSettings}
                  </button>
                </div>
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stats-section">
            {statTiles.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i }}
                className="panel stat"
              >
                <div
                  className="stat-icon"
                  style={
                    s.tone === "ok"
                      ? { background: "linear-gradient(180deg, rgba(74,222,128,.45), rgba(21,128,61,.2))", boxShadow: "inset 0 1px 0 rgba(255,255,255,.25), 0 6px 16px -6px rgba(74,222,128,.6)" }
                      : s.tone === "danger"
                      ? { background: "linear-gradient(180deg, rgba(251,113,133,.5), rgba(159,18,57,.25))", boxShadow: "inset 0 1px 0 rgba(255,255,255,.25), 0 6px 16px -6px rgba(251,113,133,.6)" }
                      : undefined
                  }
                >
                  <s.icon size={18} />
                </div>
                <div className="stat-value" data-testid={s.id}>{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Feedback table */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="panel overflow-hidden"
          data-testid="feedback-table-container"
        >
          <div className="flex items-center justify-between gap-3" style={{ padding: "1.2rem 1.25rem" }}>
            <h2 className="display" style={{ fontSize: "1.7rem" }}>{t.recentFeedback}</h2>
            {openCount > 0 && <span className="chip chip-danger chip-dot">{openCount} {t.pending.toLowerCase()}</span>}
          </div>

          {feedback.length === 0 ? (
            <div className="text-center" style={{ padding: "3.5rem 1.5rem" }}>
              <div className="bob inline-block mb-4">
                <LogoMark size={48} />
              </div>
              <p className="muted mx-auto" style={{ maxWidth: "44ch" }}>{t.noFeedback}</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data-table" data-testid="feedback-table">
                <thead>
                  <tr>
                    <th>{t.rating}</th>
                    <th>{t.message}</th>
                    <th>{t.room}</th>
                    <th>{t.date}</th>
                    <th>{t.status}</th>
                    <th style={{ textAlign: "right" }}>{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {feedback.map((item, index) => (
                    <tr key={item.id} className={item.rating <= 3 ? "is-low" : ""} data-testid={`feedback-row-${index}`}>
                      <td><MiniStars rating={item.rating} /></td>
                      <td style={{ maxWidth: 360 }}>
                        {item.message ? (
                          <span className="block" style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }} title={item.message}>
                            {item.message}
                          </span>
                        ) : (
                          <span className="faint">{t.noMessage}</span>
                        )}
                      </td>
                      <td className="mono">{item.room_number || <span className="faint">—</span>}</td>
                      <td className="muted whitespace-nowrap text-sm">{formatDate(item.created_at)}</td>
                      <td>
                        <span className={`chip chip-dot ${item.status === "resolved" ? "chip-resolved" : "chip-pending"}`}>
                          {item.status === "resolved" ? t.resolved : t.pending}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          {confirmId === item.id ? (
                            <>
                              <span className="text-sm muted mr-1">{t.confirmDelete}</span>
                              <button className="btn btn-sm" style={{ minHeight: 32, background: "var(--danger)", color: "#2a0610" }} onClick={() => handleDelete(item.id)}>
                                {t.delete}
                              </button>
                              <button className="icon-btn" onClick={() => setConfirmId(null)} aria-label="Cancel">
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              {item.status === "pending" ? (
                                <button onClick={() => handleStatusUpdate(item.id, "resolved")} className="icon-btn is-ok" title={t.markResolved} aria-label={t.markResolved} data-testid={`resolve-btn-${index}`}>
                                  <CircleCheck size={18} />
                                </button>
                              ) : (
                                <button onClick={() => handleStatusUpdate(item.id, "pending")} className="icon-btn is-warn" title={t.reopen} aria-label={t.reopen} data-testid={`pending-btn-${index}`}>
                                  <RotateCcw size={17} />
                                </button>
                              )}
                              <button onClick={() => setConfirmId(item.id)} className="icon-btn is-danger" title={t.delete} aria-label={t.delete} data-testid={`delete-btn-${index}`}>
                                <Trash2 size={17} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.section>
      </main>
    </div>
  );
};

export default AdminDashboard;
